import { useCallback } from 'react';
import { useIsClientReady } from './use-is-client-ready.tsx';

type SsrSafeCallbackFunction = {
	<TReturn>(callback: () => TReturn): TReturn | undefined;
	<TReturn, TDefaultValue>(
		callback: () => TReturn,
		defaultValue: TDefaultValue,
	): TReturn | TDefaultValue;
};

/**
 * Returns an function that can be used to wrap a function call and make it SSR/Hydration safe.
 * Note that this guarantees a re-render of your component post-hydration.
 *
 * Usage:
 * const MyComponent = () => {
 *   const safeCall = useSsrSafeCallback();
 *
 *   // `foo` will be assigned the return value from myUnsafeFunction(), or `undefined` if in SSR
 *   const foo = safeCall(() => myUnsafeFunction())
 *
 *   // `bar` will be assigned the return value from myUnsafeFunction(), or `'defaultValue'` if in SSR
 *   const bar = safeCall(() => myUnsafeFunction(), 'defaultValue')
 * }
 * @agent-skills: ssr
 */
export function useSsrSafeCallback(): SsrSafeCallbackFunction {
	const isClientReady = useIsClientReady();

	return useCallback<SsrSafeCallbackFunction>(
		(callback: () => void, defaultValue = undefined) => {
			if (!isClientReady) {
				return defaultValue;
			}

			return callback();
		},
		[isClientReady],
	);
}
